"use client"

import { AlertTriangle, CheckCircle2 } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { checkGroupAvailability } from "@/lib/appointments/group-booking"
import { getApiErrorMessage } from "@/lib/utils/api-error"
import { toastUtils } from "@/utils/toast-utils"
import { useStore } from "@/app/components/common/store.provider"
import { guestLabel } from "./GroupGuestTabs"

/**
 * Shows whether the whole party fits at the requested times and offers the
 * server's same-time and staggered alternatives as one-tap fixes.
 */
export const GroupAvailabilityPanel = observer(function GroupAvailabilityPanel() {
  const { appointmentStore, authStore } = useStore()
  const [checking, setChecking] = useState(false)

  const availability = appointmentStore.groupAvailability
  const members = appointmentStore.groupMembers

  if (appointmentStore.groupIsCustomBooking) return null

  const runCheck = async () => {
    setChecking(true)
    try {
      await checkGroupAvailability(
        appointmentStore,
        authStore.user?.id ? Number(authStore.user.id) : undefined,
      )
    } catch (error) {
      toastUtils.error(
        "Check Failed",
        getApiErrorMessage(error, "Could not check availability."),
      )
    } finally {
      setChecking(false)
    }
  }

  const applyStaggeredPlan = () => {
    if (!availability?.staggeredPlan) return
    availability.staggeredPlan.forEach((item) => {
      const member = members[item.memberIndex]
      if (member) {
        appointmentStore.updateGroupMember(member.localId, {
          startTime: item.startTime,
        })
      }
    })
    appointmentStore.setGroupAvailability(null)
    toastUtils.success("Applied", "Staggered times applied. Re-check to confirm.")
  }

  if (!availability) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Confirm every guest still fits at their chosen time.
        </p>
        <Button
          type="button"
          variant="outline"
          className="shrink-0"
          disabled={checking}
          onClick={() => void runCheck()}
        >
          {checking ? "Checking…" : "Check availability"}
        </Button>
      </div>
    )
  }

  if (availability.allFit) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-green-600/40 bg-green-500/10 p-4 text-sm">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-700 dark:text-green-300" />
        <p className="text-green-900 dark:text-green-100">
          Everyone in your party fits at the times you picked.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
        <div>
          <p className="text-sm font-medium text-foreground">
            {availability.fittingCount} of {availability.partySize} guests fit as
            requested
          </p>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            {availability.members
              .filter((m) => !m.fits)
              .map((m) => {
                const member = members[m.memberIndex]
                return (
                  <li key={m.memberIndex}>
                    <span className="font-medium text-foreground">
                      {member ? guestLabel(member, m.memberIndex) : m.beneficiaryName}
                    </span>
                    {": "}
                    {m.reasons.join("; ") || "Not available at this time."}
                  </li>
                )
              })}
          </ul>
        </div>
      </div>

      {availability.sameTimeSuggestions.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Same-time slots that fit the whole party:
          </p>
          <div className="flex flex-wrap gap-2">
            {availability.sameTimeSuggestions.map((suggestion) => (
              <button
                key={suggestion.startTime}
                type="button"
                onClick={() => {
                  appointmentStore.setAllGroupStartTimes(suggestion.startTime)
                  toastUtils.success(
                    "Applied",
                    `All guests set to ${suggestion.startTime}. Re-check to confirm.`,
                  )
                }}
                className="min-h-11 rounded-full border bg-background px-4 text-xs hover:bg-muted"
              >
                {suggestion.startTime} · {suggestion.availableStaff} free
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {availability.staggeredPlan?.length ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Staggered plan that fits everyone:
          </p>
          <div className="space-y-1 text-xs text-muted-foreground">
            {availability.staggeredPlan.map((item) => (
              <div key={item.memberIndex}>
                {item.beneficiaryName || `Guest ${item.memberIndex + 1}`}:{" "}
                {item.startTime}–{item.endTime}
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={applyStaggeredPlan}>
            Apply staggered plan
          </Button>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={checking}
          onClick={() => void runCheck()}
        >
          {checking ? "Checking…" : "Re-check availability"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => appointmentStore.setGroupIsCustomBooking(true)}
        >
          Let the salon schedule us instead
        </Button>
      </div>
    </div>
  )
})
